import { Draggable, Droppable } from '@hello-pangea/dnd';
import { useVirtualizer } from '@tanstack/react-virtual';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { ds } from '@/config/colors';

// Kontener scrollowany — ograniczona wysokość włącza wirtualizację (renderujemy
// tylko karty widoczne w oknie, nie całą — potencjalnie ogromną — kolumnę).
const ScrollBody = styled.div<{ $isDraggingOver: boolean; $offset: number }>`
  flex: 1;
  min-height: 320px;
  /* Wysokość rośnie z ekranem — kolumna wypełnia widok zamiast ucinać się na
     sztywnej wartości; na małych ekranach broni jej min-height. */
  max-height: calc(100vh - ${({ $offset }) => $offset}px);
  overflow-y: auto;
  overflow-x: hidden;
  padding: 10px;
  transition: background 0.15s;

  /* Pasek przewijania widoczny tylko po najechaniu na kolumnę. Miejsce na niego jest
     zarezerwowane zawsze (scrollbar-gutter: stable), więc karty nie zwężają się na hover. */
  scrollbar-gutter: stable;
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: transparent;
    border-radius: 9999px;
  }

  &:hover {
    scrollbar-color: rgba(15, 23, 42, 0.22) transparent;
  }

  &:hover::-webkit-scrollbar-thumb {
    background: rgba(15, 23, 42, 0.22);
  }
  background: ${({ $isDraggingOver, theme }) => {
    if (!$isDraggingOver) return 'transparent';
    return theme.mode === 'dark' ? 'rgba(59, 130, 246, 0.08)' : 'rgba(59, 130, 246, 0.06)';
  }};
`;

// Ręczne doczytanie, gdy kolumna nie ma scrolla, a w bazie jest więcej (#375).
const LoadMoreButton = styled.button`
  width: 100%;
  min-height: 40px;
  margin-top: 4px;
  padding: 8px 12px;
  border: 1px dashed ${ds.borderStrong};
  border-radius: 10px;
  background: transparent;
  color: ${ds.textSecondary};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.15s, border-color 0.15s;

  &:hover {
    color: ${ds.textPrimary};
    border-color: ${ds.textSecondary};
  }

  &:focus-visible {
    outline: 2px solid ${ds.textPrimary};
    outline-offset: 2px;
  }
`;

interface KanbanVirtualColumnProps<T> {
  droppableId: string;
  items: T[];
  getId: (item: T) => string;
  // Renderuje SAM wizual karty (bez propsów dnd — te wpina komponent na wrapperze).
  renderCard: (item: T, isDragging: boolean) => ReactNode;
  /**
   * Karty, których nie wolno przeciągać (np. zamówienie czekające na płatność).
   * Blokada jest tutaj, a nie w `onDragEnd` — karta, która daje się złapać, obiecuje
   * zmianę statusu, a potem cicho wraca na miejsce.
   */
  isItemDragDisabled?: (item: T) => boolean;
  emptyState?: ReactNode;
  // Szacowana wysokość karty (px) — dokładna liczona jest dynamicznie (measureElement).
  estimateSize?: number;
  // Pionowy margines (px) nad boardem (nagłówek strony + toolbar + filtry) — kolumna
  // dostaje wysokość `100vh - offset`, żeby na dużych ekranach wypełniała widok, a nie
  // ucinała się na sztywnej wartości.
  heightOffset?: number;
  /** Wywołane przy zbliżeniu do końca scrolla (lazy load kolejnej paczki). */
  onNearEnd?: () => void;
  /** Odległość od dołu (px), przy której triggujemy `onNearEnd`. */
  nearEndOffset?: number;
}

// Wirtualizowana kolumna kanban zintegrowana z @hello-pangea/dnd (tryb `virtual`
// + `renderClone`). Renderuje tylko karty w oknie widoku — kolumny z setkami
// zakończonych zgłoszeń/zamówień nie zabijają już DOM-u.
export function KanbanVirtualColumn<T>({
  droppableId,
  items,
  getId,
  renderCard,
  isItemDragDisabled,
  emptyState,
  estimateSize = 150,
  heightOffset = 320,
  onNearEnd,
  nearEndOffset = 240,
}: KanbanVirtualColumnProps<T>) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const onNearEndRef = useRef(onNearEnd);
  onNearEndRef.current = onNearEnd;

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => estimateSize,
    overscan: 6,
    // Spacing między kartami wliczamy w pomiar wrappera (paddingBottom), więc
    // measureElement zwraca pełną „zajętą" wysokość.
  });

  // Load-on-scroll: gdy użytkownik zbliża się do dołu kolumny (albo kolumna
  // nie jest jeszcze przewijalna), prosimy rodzica o dociągnięcie kolejnej paczki.
  // Effect zależy od tego, CZY jest `onNearEnd`, a nie od jego tożsamości (wołamy przez ref).
  // Rodzic podaje nową strzałkę w każdym renderze, więc przy zależności od funkcji „pierwszy
  // check” odpalał się po każdym renderze: kolumna z kilkoma widocznymi kartami i wieloma
  // ukrytymi (zarchiwizowane dostarczone) doczytywała stronę za stroną aż do limitu (#375).
  // Teraz kolejny check bez scrolla jest tylko wtedy, gdy przybyło widocznych kart.
  // Bez automatu zostaje przypadek: kolumna bez scrolla, a doczytana paczka to same ukryte
  // karty (np. zarchiwizowane). Wtedy pokazujemy „Pokaż więcej” do ręcznego doczytania.
  const { t } = useTranslation();
  const [canScroll, setCanScroll] = useState(false);
  const hasNearEnd = Boolean(onNearEnd);
  useEffect(() => {
    if (!hasNearEnd) return undefined;
    const el = scrollRef.current;
    if (!el) return undefined;

    let ticking = false;
    const check = () => {
      ticking = false;
      setCanScroll(el.scrollHeight > el.clientHeight + 1);
      // Pusta kolumna nie trigguje dociągania (inaczej 3 puste × 1 pełna
      // odpalałyby load w pętli przy starcie).
      if (items.length === 0) return;
      const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (remaining < nearEndOffset) {
        onNearEndRef.current?.();
      }
    };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(check);
      }
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    // Pierwszy check — gdy kart jest mało i nie ma scrollbara, i tak dociągamy.
    check();
    return () => el.removeEventListener('scroll', onScroll);
  }, [hasNearEnd, nearEndOffset, items.length]);

  return (
    <Droppable
      droppableId={droppableId}
      mode="virtual"
      renderClone={(provided, snapshot, rubric) => {
        const item = items[rubric.source.index];
        return (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            style={provided.draggableProps.style}
          >
            {item != null && renderCard(item, snapshot.isDragging)}
          </div>
        );
      }}
    >
      {(provided, snapshot) => (
        <ScrollBody
          ref={(el) => {
            scrollRef.current = el;
            provided.innerRef(el);
          }}
          {...provided.droppableProps}
          $isDraggingOver={snapshot.isDraggingOver}
          $offset={heightOffset}
        >
          {items.length === 0 && !snapshot.isDraggingOver && emptyState}

          <div
            style={{
              height: virtualizer.getTotalSize(),
              width: '100%',
              position: 'relative',
            }}
          >
            {virtualizer.getVirtualItems().map((vItem) => {
              const item = items[vItem.index];
              if (item == null) return null;
              return (
                <Draggable
                  key={getId(item)}
                  draggableId={getId(item)}
                  index={vItem.index}
                  isDragDisabled={isItemDragDisabled?.(item) ?? false}
                >
                  {(dragProvided, dragSnapshot) => (
                    <div
                      ref={(el) => {
                        dragProvided.innerRef(el);
                        if (el) virtualizer.measureElement(el);
                      }}
                      data-index={vItem.index}
                      {...dragProvided.draggableProps}
                      {...dragProvided.dragHandleProps}
                      style={{
                        ...dragProvided.draggableProps.style,
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        transform: `translateY(${vItem.start}px)`,
                      }}
                    >
                      <div style={{ paddingBottom: 10 }}>
                        {renderCard(item, dragSnapshot.isDragging)}
                      </div>
                    </div>
                  )}
                </Draggable>
              );
            })}
          </div>

          {hasNearEnd && !canScroll && !snapshot.isDraggingOver && (
            <LoadMoreButton type="button" onClick={() => onNearEndRef.current?.()}>
              {t('common.showMore', 'Pokaż więcej')}
            </LoadMoreButton>
          )}
        </ScrollBody>
      )}
    </Droppable>
  );
}
